import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, TrendingUp, Clock, Server } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'AI Email Parsing Engine', icon: Cloud },
  { name: 'IMPA / ISSA Code Matching', icon: Database },
  { name: 'Vendor Catalogue Integration', icon: Server },
  { name: 'Margin Protection Logic', icon: Shield },
  { name: 'Automated Quote Generation', icon: FileText },
];

const keyMetrics = [
  {
    icon: Clock,
    value: '30 Min',
    label: 'Quote Turnaround',
    description: 'Reduced from 2–3 days per RFQ',
  },
  {
    icon: TrendingUp,
    value: '$100K+',
    label: 'Annual Labor Recovered',
    description: 'Per maritime distributor deployment',
  },
  {
    icon: CheckCircle2,
    value: 'Zero',
    label: 'Pricing Errors',
    description: 'Since go-live across all processed RFQs',
  },
  {
    icon: Database,
    value: '847+',
    label: 'Line Items',
    description: 'Processed accurately in a single RFQ',
  },
];

const highlights = [
  {
    category: 'Speed and Accuracy',
    items: [
      'Reduced quote turnaround from 2–3 days to under 30 minutes through complete end-to-end workflow automation',
      'Eliminated all pricing errors since go-live through automated margin protection checks on every line item',
      'Processed 847+ line items in a single RFQ without performance degradation or manual review requirement',
    ],
  },
  {
    category: 'Commercial Impact',
    items: [
      'Recovered $100K+ in annual labor costs per maritime distributor through eliminated manual quoting overhead',
      'Deployed in an average of 1 week per client with minimal configuration requirements',
      'Delivered zero human intervention in the complete pricing workflow from email receipt to quote delivery',
    ],
  },
];

const challenges = [
  {
    title: 'Completely Inconsistent RFQ Formats',
    icon: FileText,
    problem: 'Ship chandler RFQs arrive in completely inconsistent formats — different layouts, abbreviations, languages, and product naming conventions — making any rules-based extraction fail systematically.',
    solution: 'Built an AI email parsing engine capable of extracting and normalizing line items regardless of input format, handling messy and abbreviated maritime product descriptions without any manual preprocessing step.',
    impact: 'Automated extraction across all customer RFQ formats with zero manual preparation required',
  },
  {
    title: 'IMPA/ISSA Code Matching at Scale',
    icon: Database,
    problem: 'Maritime products must be matched to standard IMPA and ISSA codes for catalogue lookup and pricing — manual matching across hundreds of line items per RFQ was slow, error-prone, and a bottleneck to quoting speed.',
    solution: 'Integrated IMPA and ISSA code databases directly into the parsing pipeline, enabling automated code matching at the point of item identification rather than as a separate manual downstream step.',
    impact: 'Accurate IMPA/ISSA code matching across 847+ line items with zero manual intervention',
  },
  {
    title: 'Unprotected Pricing Margins',
    icon: Shield,
    problem: 'Manual quoting resulted in frequent pricing errors and margin erosion — items were regularly quoted below acceptable margins with no systematic check in place before quotes left the business.',
    solution: 'Built automated margin protection into the quote generation engine as a mandatory gate — no quote can be produced with below-threshold margins, with exceptions flagged for review before delivery.',
    impact: 'Zero pricing errors since go-live across all processed RFQs',
  },
  {
    title: '2–3 Day Turnaround Losing Business to Competitors',
    icon: Clock,
    problem: 'In maritime supply, slow quote response directly loses business — 2–3 day turnaround was uncompetitive and consumed the entire capacity of the quoting team for a single vessel\'s requirements.',
    solution: 'Automated the complete workflow from email receipt through parsing, IMPA/ISSA matching, pricing, margin validation, and formatted quote generation — delivering the complete quote in under 30 minutes with no manual steps.',
    impact: 'Quote turnaround reduced from 2–3 days to under 30 minutes, recovering $100K+ annually per deployment',
  },
];

const ShipChandlerRFQ = () => {
  return (
    <>
      <SEOHead
        title="AI-Native Ship Chandler RFQ Automation Case Study | Agility AI"
        description="How Agility automated maritime RFQ quoting for ship chandlers — reducing turnaround from 2–3 days to 30 minutes, recovering $100K+ annually, with zero pricing errors since go-live."
        url="https://www.agilitytech.ai/case-studies/ship-chandler-rfq"
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "AI-Native Ship Chandler RFQ Automation",
          "description": "How Agility automated maritime RFQ quoting for ship chandlers — reducing turnaround from 2–3 days to 30 minutes, recovering $100K+ annually, with zero pricing errors since go-live.",
          "author": { "@type": "Organization", "name": "Agility" },
          "publisher": {
            "@type": "Organization",
            "name": "Agility",
            "logo": { "@type": "ImageObject", "url": "https://www.agilitytech.ai/agility hex fonts.png" }
          },
          "datePublished": "2025-10-01",
          "dateModified": "2026-05-01",
          "mainEntityOfPage": { "@type": "WebPage", "@id": "https://www.agilitytech.ai/case-studies/ship-chandler-rfq" }
        }}
      />
      <div className="pt-20 bg-black">
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src="https://images.unsplash.com/photo-1519452575417-564c1401ecc0?auto=format&fit=crop&q=80" alt="Banner" className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">Maritime</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">AI-Native Ship Chandler RFQ Automation</h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">Eliminating 2–3 day manual quoting cycles for ship chandlers through AI-powered RFQ email parsing, automated IMPA/ISSA code matching, and margin-protected quote generation in under 30 minutes.</p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50"><h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3><p className="text-white">Maritime</p></div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50"><h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3><p className="text-white">Caribbean</p></div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50"><h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3><p className="text-white">Multi-Distributor Deployment</p></div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50"><h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3><p className="text-white">1 Week Average Deployment Per Client</p></div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gray-900 border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {technologies.map((tech, index) => (
                <div key={index} className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30">
                  <tech.icon className="h-6 w-6 text-teal-400" />
                  <span className="text-gray-300 font-medium">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Manual Excel-Based Maritime Quoting</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">Maritime distributors and ship chandlers processing RFQs containing hundreds of line items relied entirely on manual Excel-based workflows — each RFQ taking 2–3 days to quote with frequent pricing errors, unprotected margins, and no standardized IMPA/ISSA code matching. Customer emails arrived in completely inconsistent formats with abbreviations, mixed item descriptions, and non-standard product references. The speed of quote delivery directly determined whether business was won or lost to competitors, and the manual process was consuming the entire operational capacity of the team.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-black border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Fully Automated Maritime Quoting Platform</h3>
                    <p className="text-xl text-gray-300 leading-relaxed">We replaced the 2–3 day manual quoting process with a fully automated end-to-end platform — AI parses incoming RFQ emails regardless of format, matches line items to IMPA/ISSA codes, pulls live vendor pricing, applies mandatory margin protection, and generates a professional formatted quote in under 30 minutes with zero human intervention. Each deployment takes one week. Each deployment recovers $100K+ in annual labor. Zero pricing errors since go-live across all client deployments.</p>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {highlights.map((category, index) => (
                  <div key={index} className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
                    <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                    <div className="space-y-4">
                      {category.items.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                          <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <challenge.icon className="h-6 w-6 text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center"><span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>Problem</h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>Solution</h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                    </div>
                    <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                      <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                      <p className="text-teal-300 font-medium">{challenge.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">Ready to Automate Your Maritime Quoting?</h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">Contact our automation specialists to discover how AI-powered RFQ processing can eliminate manual quoting cycles and recover significant labor costs for your maritime business.</p>
              <Link to="/contact" className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                <span>Get Started Today</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShipChandlerRFQ;
